import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FilterIcon from '@mui/icons-material/TuneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import Modal from '@/components/Modals/Modal';
import SearchInput from '@/components/SearchInput';
import { Order } from '@/types/fulfill/inventoryHistory';

import DownloadModal from './dependencies/DownloadModal/DownloadModal';
import { inventoryColumns } from './inventoryHistory-columns';
import InventoryHistoryFilters from './InventoryHistoryFilters';
import { mapLotBreakdown } from './mapLotBreakdown';
import PickupsModal from './PickupsModal';
import useInventoryHistory from './useInventoryHistory';
import { filters } from './utils';

const DETAIL_PANEL_CHROME_HEIGHT = 54;
const DETAIL_PANEL_ROW_HEIGHT = 33;
const DETAIL_PANEL_TOTALS_EXTRA_PADDING = 10;
const DETAIL_PANEL_EMPTY_STATE_HEIGHT = 53;
const MAIN_ROW_HEIGHT = 76;

const InventoryHistoryTable = () => {
  const hook = useInventoryHistory();
  const theme = useTheme();
  const { t } = useTranslation('commonGeneral');

  const rows = useMemo(() => {
    const result: Array<Order | Record<string, unknown>> = [];
    hook.orders.data.forEach((order) => {
      result.push(order);
      const expandState = hook.expandedRows[order.aoNumber];
      if (expandState) {
        result.push({ id: `${order.aoNumber}__detail`, isDetailRow: true, order, expandState });
      }
    });
    return result;
  }, [hook.orders.data, hook.expandedRows]);

  const getRowId = useCallback(
    (row: { isDetailRow?: boolean; id?: string; aoNumber?: string }) =>
      row.isDetailRow ? (row.id as string) : (row.aoNumber as string),
    [],
  );

  const getRowHeight = useCallback(
    (params: { model: { isDetailRow?: boolean; order?: Order } }) => {
      if (!params.model.isDetailRow || !params.model.order) return undefined;
      const lotCount = mapLotBreakdown(params.model.order).length;
      if (lotCount === 0) {
        return DETAIL_PANEL_CHROME_HEIGHT + DETAIL_PANEL_EMPTY_STATE_HEIGHT;
      }
      const rowCount = lotCount + 2;
      return (
        DETAIL_PANEL_CHROME_HEIGHT +
        rowCount * DETAIL_PANEL_ROW_HEIGHT +
        DETAIL_PANEL_TOTALS_EXTRA_PADDING
      );
    },
    [],
  );

  const getRowClassName = useCallback(
    (params: { row: { isDetailRow?: boolean } }) =>
      params.row.isDetailRow ? 'lot-breakdown-detail-row' : '',
    [],
  );

  return (
    <Paper elevation={3} sx={{ padding: `${theme.spacing(2)} 0 0` }}>
      <Box
        sx={{
          padding: `0 ${theme.spacing(4)}`,
          flexDirection: { xxs: 'column', xl: 'row' },
          gap: { xxs: '0.5rem', xl: 0 },
          alignItems: 'center',
        }}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        height="auto"
      >
        <SearchInput
          placeholder="Buscar orden"
          buttonText="Buscar"
          handleKeyPress={hook.handleKeyPress}
          onChange={hook.onChgSearch}
          onClick={hook.onSearch}
          sx={{ width: '100%' }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xxs: 'column', xs: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem',
          }}
        >
          <Box
            display="flex"
            gap="0.5rem"
            alignItems="center"
            justifyContent="center"
            sx={{
              cursor: 'pointer',
              ':hover': { backgroundColor: theme.palette.primary.lighter },
              padding: '0.5rem',
              borderRadius: '1rem',
            }}
            onClick={hook.handleOpenDrawer}
          >
            <FilterIcon color="primary" />

            <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
              Filtros
            </Typography>
          </Box>

          <Button
            onClick={() => hook.setOpenDownloadModal(true)}
            variant="text"
            size="small"
            startIcon={<CloudDownloadOutlinedIcon color="primary" />}
          >
            Descargar reporte
          </Button>
        </Box>
      </Box>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Box sx={{ minWidth: '900px' }}>
          <AgaveDataGrid
            columns={inventoryColumns({
              t,
              downloadLabels: hook.downloadLabels,
              downloadSkuLabels: hook.downloadSkuLabels,
              downloadResume: hook.downloadResume,
              onShowPickups: hook.onShowPickups,
              expandedRows: hook.expandedRows,
              toggleLotBreakdown: hook.toggleLotBreakdown,
              finalizeLotCollapse: hook.finalizeLotCollapse,
              country: hook.country,
            })}
            rows={rows}
            localeText={{
              noRowsLabel: hook.hasActiveFilters()
                ? 'No encontramos ninguna orden que coincida con tu búsqueda.'
                : 'No hay envíos disponibles',
            }}
            rowCount={hook.orders.totalRows}
            rowHeight={MAIN_ROW_HEIGHT}
            disableRowSelectionOnClick={true}
            disableCursorPointer={true}
            autoHeight
            getRowId={getRowId}
            getRowHeight={getRowHeight}
            getRowClassName={getRowClassName}
            hideFooterSelectedRowCount={true}
            paginationModel={{
              page: hook.pagination.filters.page as number,
              pageSize: hook.pagination.filters.page_size as number,
            }}
            disableColumnMenu={true}
            paginationMode="server"
            onPaginationModelChange={hook.pagination.onPaginationModelChange}
            sx={{
              '& .lot-breakdown-detail-row': {
                cursor: 'default !important',
              },
              '& .lot-breakdown-detail-row:hover': {
                backgroundColor: 'transparent !important',
              },
              '& .lot-breakdown-detail-row [data-field="orderId"]': {
                left: '0px !important',
                width: '100% !important',
                padding: '0 !important',
              },
            }}
          />
        </Box>
      </Box>
      <GenericDrawer
        open={hook.openDrawer}
        onClose={hook.handleOpenDrawer}
        title={'Filtros'}
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <InventoryHistoryFilters
          selectedFilters={hook.filters}
          onChange={hook.onChangeFilter}
          clearFilters={hook.clearFilters}
          applyFilters={hook.applyFilters}
          filters={filters}
          isClearPending={hook.isClearPending}
          isApplyPending={hook.isApplyPending}
        />
      </GenericDrawer>
      <PickupsModal
        open={hook.openPickupsModal}
        order={hook.selectedOrder}
        onClose={hook.handleClosePickupsModal}
      />
      <Modal
        isOpen={hook.openDownloadModal}
        onClose={() => hook.setOpenDownloadModal(false)}
        title="Descargar reporte"
      >
        <DownloadModal downloadReport={hook.downloadReport} />
      </Modal>
    </Paper>
  );
};

export default InventoryHistoryTable;
